import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { fetchMonthlyHoursWorked } from "../api";
import { useNavigate } from 'react-router-dom';
import { formatHoursMinutes } from "../utils/time";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";

const getActivityColor = (percentage) => {
  if (percentage <= 50) return 'rgb(255, 0, 0)';
  if (percentage >= 95) return 'rgb(0, 255, 0)';
  const factor = (percentage - 50) / 45;
  if (factor < 0.5) {
    const orangeFactor = factor * 2;
    return `rgb(255, ${Math.round(165 * orangeFactor)}, 0)`;
  } else {
    const greenFactor = (factor - 0.5) * 2;
    return `rgb(${Math.round(255 * (1 - greenFactor))}, ${Math.round(165 + (255 - 165) * greenFactor)}, 0)`;
  }
};

const MonthlyHoursWorkedSummaryTable = ({ date, onLoadingChange }) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const { formatCurrency } = useCustomerCurrency();

  useEffect(() => {
    if (!date) {
      setData([]);
      setIsLoading(false);
      onLoadingChange?.(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchMonthlyHoursWorked(date)
      .then(apiData => {
        if (cancelled) return;
        setData(apiData || []);
      })
      .catch(() => {
        if (cancelled) return;
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
        onLoadingChange?.(false);
      });
    return () => { cancelled = true; };
  }, [date]);

  // Group and sum per workcell
  const workcellMap = {};
  data.forEach(row => {
    if (!workcellMap[row.workcell_id]) {
      workcellMap[row.workcell_id] = {
        workcell_name: row.workcell_name,
        workcell_id: row.workcell_id,
        active: 0,
        wallclock: 0,
        inactive: 0,
        turnover: 0,
        hourly_rate: 0,
        days: 0
      };
    }
    workcellMap[row.workcell_id].active += row.active_hours || 0;
    workcellMap[row.workcell_id].wallclock += row.wallclock_hours || 0;
    workcellMap[row.workcell_id].inactive += row.inactive_hours || 0;
    workcellMap[row.workcell_id].turnover += row.internal_turnover || 0;
    if (!workcellMap[row.workcell_id].hourly_rate && row.hourly_rate) workcellMap[row.workcell_id].hourly_rate = row.hourly_rate;
    workcellMap[row.workcell_id].days += 1;
  });
  Object.values(workcellMap).forEach(row => {
    row.activity = row.wallclock > 0 ? (row.active / row.wallclock) * 100 : 0;
  });
  const rows = Object.values(workcellMap);
  const allWorkcellIds = rows.map(r => r.workcell_id).sort((a, b) => a - b);
  const totalActive = rows.reduce((sum, r) => sum + r.active, 0);
  const totalWallclock = rows.reduce((sum, r) => sum + r.wallclock, 0);
  const totalInactive = rows.reduce((sum, r) => sum + r.inactive, 0);
  const totalTurnover = rows.reduce((sum, r) => sum + r.turnover, 0);
  const avgActivity = totalWallclock > 0 ? (totalActive / totalWallclock) * 100 : 0;
  const formatPct = (value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(safe / 100);
  };

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (rows.length === 0) return <div>{t("common.noData")}</div>;

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>{t("monthly.hoursWorked.table.workcell")}</TableCell>
          <TableCell align="right">{t("monthly.hoursWorked.table.activeHours")}</TableCell>
          <TableCell align="right">{t("monthly.hoursWorked.table.inactiveHours")}</TableCell>
          <TableCell align="right">{t("monthly.hoursWorked.table.internalTurnover")}</TableCell>
          <TableCell align="right">{t("monthly.hoursWorked.table.activityPct")}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.workcell_id}>
            <TableCell
              component="th"
              scope="row"
              sx={{ color: getWorkcellColor(row.workcell_id, theme.palette.chart.colors, allWorkcellIds), fontWeight: 'bold' }}
            >
              {row.workcell_name}
            </TableCell>
            <TableCell align="right">{formatHoursMinutes(row.active)}</TableCell>
            <TableCell align="right">{formatHoursMinutes(row.inactive)}</TableCell>
            <TableCell align="right">{formatCurrency(row.turnover)}</TableCell>
            <TableCell align="right" sx={{ position: 'relative', '&::after': { content: '""', position: 'absolute', right: 0, top: 0, width: '8px', height: '100%', backgroundColor: getActivityColor(row.activity), opacity: 0.8 } }}>{formatPct(row.activity)}</TableCell>
            <TableCell align="right">
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/chart/workcell/${row.workcell_id}/monthly/${date}`)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t("common.viewDetails")}
              </Button>
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
          <TableCell component="th" scope="row">{t("common.total")}</TableCell>
          <TableCell align="right">{formatHoursMinutes(totalActive)}</TableCell>
          <TableCell align="right">{formatHoursMinutes(totalInactive)}</TableCell>
          <TableCell align="right">{formatCurrency(totalTurnover)}</TableCell>
          <TableCell align="right" sx={{ position: 'relative', '&::after': { content: '""', position: 'absolute', right: 0, top: 0, width: '8px', height: '100%', backgroundColor: getActivityColor(avgActivity), opacity: 0.8 } }}>{formatPct(avgActivity)}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default MonthlyHoursWorkedSummaryTable;
